export const PREVIEW_ENV_ID = "preview-env-00000000";

const PREVIEW_MODE_STORAGE_KEY = "previewMode";
// sessionStorage, so the opt-out is scoped to one tab and dies with it while
// other tabs in the browser keep showing preview data.
const PREVIEW_MODE_TAB_OPT_OUT_STORAGE_KEY = "previewModeTabOptOut";

let cachedEnabled: boolean | null = null;

export function isPreviewModeEnabled(): boolean {
  if (cachedEnabled !== null) return cachedEnabled;
  cachedEnabled = isPreviewModeOnGlobally() && !isTabOptedOutOfPreviewMode();
  return cachedEnabled;
}

export function isPreviewModeOnGlobally(): boolean {
  try {
    return localStorage.getItem(PREVIEW_MODE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function isTabOptedOutOfPreviewMode(): boolean {
  try {
    return (
      sessionStorage.getItem(PREVIEW_MODE_TAB_OPT_OUT_STORAGE_KEY) === "true"
    );
  } catch {
    return false;
  }
}

// "preview": this tab renders synthetic data. "realData": preview is on in
// this browser but this tab renders real data, because it opted out or loaded
// before preview was turned on. The tab half is cached at load and the global
// half is read live, so the two can disagree until the tab reloads.
export type TabPreviewState = "off" | "preview" | "realData";

export function getTabPreviewState(): TabPreviewState {
  if (isPreviewModeEnabled()) return "preview";
  return isPreviewModeOnGlobally() ? "realData" : "off";
}

// Marks preview tabs in the browser tab strip, so a tab showing synthetic data
// can be told apart from one showing real data without switching to it.
export function applyPreviewModeFavicon(): void {
  if (!isPreviewModeEnabled()) return;
  document
    .querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
    .forEach((link) => {
      link.href = "/favicon-preview.png";
    });
}

export function optTabOutOfPreviewMode(): void {
  try {
    sessionStorage.setItem(PREVIEW_MODE_TAB_OPT_OUT_STORAGE_KEY, "true");
  } catch {
    // sessionStorage may be unavailable; the reload still re-reads the flags.
  }
  window.location.reload();
}

export function optTabIntoPreviewMode(): void {
  try {
    sessionStorage.removeItem(PREVIEW_MODE_TAB_OPT_OUT_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable; the reload still re-reads the flags.
  }
  window.location.reload();
}

export function enablePreviewMode(): void {
  localStorage.setItem(PREVIEW_MODE_STORAGE_KEY, "true");
  // Turning preview on from this tab means this tab should show it, even if
  // it was opted out earlier.
  sessionStorage.removeItem(PREVIEW_MODE_TAB_OPT_OUT_STORAGE_KEY);
  // Full reload so module-level caches (the runtime flag, the synthetic
  // graph) reset cleanly and every read kind goes through the preview
  // handlers.
  window.location.reload();
}

export function disablePreviewMode(): void {
  localStorage.removeItem(PREVIEW_MODE_STORAGE_KEY);
  // Full reload so module-level caches (the runtime flag, the synthetic
  // graph) reset cleanly and every read kind goes back to the network.
  window.location.reload();
}

// Clears the preview-mode flag without reloading. Use this on login paths so
// every authenticated session starts with preview mode off, regardless of
// whether the prior session was logged out due to inactivity, a manual signout,
// or simply closing the tab while the flag was set.
export function clearPreviewModeFlag(): void {
  try {
    localStorage.removeItem(PREVIEW_MODE_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable (private mode, disabled cookies); the
    // module-level cache still wins for the rest of the session.
  }
  cachedEnabled = null;
}

// Clears the preview-mode flag only on a real auth-state transition. Firebase's
// onAuthStateChanged listener fires once with the *restored* auth state when
// the listener registers — that initial read looks identical to a sign-in but
// is just the continuation of an existing session. Treating it as a transition
// would wipe a flag the user just set and then refreshed to apply.
export function clearPreviewModeOnAuthTransition(
  previous: boolean | undefined,
  current: boolean,
): void {
  if (previous === undefined || previous === current) return;
  clearPreviewModeFlag();
}

export function __resetPreviewModeCache(): void {
  cachedEnabled = null;
}
